import type { ExperimentLayer } from '@atlassian/search-client';

import type { useResultSupplierAdvancedSearchCQLQueryVariables as AdvancedSearchCQLQueryVariables } from '../__types__/useResultSupplierAdvancedSearchCQLQuery.ts';
import type { useResultSupplierAdvancedAGGSearchQueryVariables as AdvancedAGGSearchQueryVariables } from '../__types__/useResultSupplierAdvancedAGGSearchQuery.ts';
import type { FilterStateType } from '../AdvancedSearch-types.ts';
import { createAggFilters } from './filters.tsx';
import {
	PAGE_SIZE,
	DEFAULT_ENTITIES,
	CONFLUENCE_AS_EXPERIENCE,
	ConfluenceATIs,
	FilterIds,
} from '../AdvancedSearch-constants.ts';

export const getQueryVariablesForCQLSearch = (
	cql: string | string[],
): {
	variables: AdvancedSearchCQLQueryVariables;
} => {
	if (Array.isArray(cql)) {
		cql = cql[0];
	}

	return {
		variables: {
			cql,
			first: PAGE_SIZE,
		},
	};
};

type getQueryVariablesForAggSearchArgsType = {
	searchQueryText: string;
	selectedFilters: FilterStateType;
	confluenceInstance: string;
	searchSessionId: string;
	userId: string | null;
	activationId: string | null;
	isWhiteboardsEnabled?: boolean;
	isDatabasesEnabled?: boolean;
	isLivePagesEnabled?: boolean;
	experimentId?: string;
	experimentLayers?: ExperimentLayer[];
};

export const getQueryVariablesForAggSearch = ({
	searchQueryText,
	selectedFilters,
	confluenceInstance,
	searchSessionId,
	activationId,
	isWhiteboardsEnabled = false,
	isDatabasesEnabled = false,
	isLivePagesEnabled = false,
	experimentId,
	experimentLayers,
}: getQueryVariablesForAggSearchArgsType): {
	variables: AdvancedAGGSearchQueryVariables;
} => {
	return {
		variables: {
			experience: CONFLUENCE_AS_EXPERIENCE,
			analytics: {
				searchSessionId,
			},
			query: searchQueryText || '',
			first: PAGE_SIZE,
			filters: {
				entities: getEntities({
					filters: selectedFilters,
					isDatabasesEnabled,
					isWhiteboardsEnabled,
				}),
				locations: [confluenceInstance],
				confluenceFilters: createAggFilters(selectedFilters, activationId),
			},
			experimentContext: {
				experimentId,
				experimentLayers,
			},
			isLivePagesEnabled,
		},
	};
};

export const getEntities = ({
	filters,
	isDatabasesEnabled,
	isWhiteboardsEnabled,
}: {
	filters: FilterStateType;
	isDatabasesEnabled?: boolean;
	isWhiteboardsEnabled?: boolean;
}) => {
	let entities = DEFAULT_ENTITIES;
	if (isWhiteboardsEnabled) {
		entities = entities.concat([ConfluenceATIs.WHITEBOARD]);
	}
	if (isDatabasesEnabled) {
		entities = entities.concat([ConfluenceATIs.DATABASE]);
	}
	// https://hello.atlassian.net/wiki/spaces/~638712e7f48fbd9b62d80f9a/pages/2353884324/Confluence+Search+Filter+by+Content+Type
	// shows how entities need to be set for Confluence and special content types
	const selectedTypeFilters = filters[FilterIds.TYPE].filter((typeOption) => typeOption.isSelected);
	if (selectedTypeFilters.length > 0) {
		// only one type can be selected at a time
		const selectedFilter = selectedTypeFilters[0];
		switch (selectedFilter.value) {
			case 'page':
				entities = [ConfluenceATIs.PAGE];
				break;
			case 'blogpost':
				entities = [ConfluenceATIs.BLOG];
				break;
			case 'attachment':
				entities = [ConfluenceATIs.ATTACHMENT];
				break;
			case 'space':
				entities = [ConfluenceATIs.SPACE];
				break;
			case 'whiteboard':
				if (isWhiteboardsEnabled) {
					entities = [ConfluenceATIs.WHITEBOARD];
				}
				break;
			case 'database':
				if (isDatabasesEnabled) {
					entities = [ConfluenceATIs.DATABASE];
				}
				break;
			case 'embed':
				entities = [ConfluenceATIs.EMBED];
				break;
			case 'folder':
				entities = [ConfluenceATIs.FOLDER];
				break;
			case 'comment':
				entities = [ConfluenceATIs.COMMENT];
				break;
			default:
				// special content types are populated dynamically in type filter drop down pane
				// by the cgraphQL ContentTypesQuery and need 'confluence.' prepended
				const specialContentType = `confluence.${selectedFilter.value}`;
				entities = [specialContentType];
		}
	}

	return entities;
};
